/**
 * Integrations Page - Google & Social Media Setup
 * 
 * Shows setup guides for external platform integrations.
 * Includes product feed URLs for Google Merchant and Meta Commerce.
 * All integrations start as "not connected" until Tammy sets them up.
 */

import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  HiExternalLink, 
  HiCheckCircle, 
  HiExclamationCircle,
  HiClipboardCopy,
  HiRefresh,
  HiLink,
  HiX
} from 'react-icons/hi';
import { 
  FaGoogle, 
  FaFacebook, 
  FaInstagram, 
  FaShoppingCart,
  FaRss,
  FaSquare
} from 'react-icons/fa';
import { isGa4MeasurementIdConfigured } from '../../lib/ga4Config';
import { adminFetch } from '../lib/adminApi';

type IntegrationStatus = 'connected' | 'not_connected';

interface Integration {
  id: string;
  name: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  iconBg: string;
  iconColor: string;
  status: IntegrationStatus;
  setupUrl: string;
  features: string[];
}

// All integrations start as not_connected - Tammy will set these up
const integrations: Integration[] = [
  {
    id: 'google-business',
    name: 'Google Business Profile',
    description: 'Appear in Google Maps and local search results',
    icon: FaGoogle,
    iconBg: 'bg-white',
    iconColor: 'text-[#4285F4]',
    status: 'connected', // Verified: Website link is working on Google Maps
    setupUrl: 'https://business.google.com',
    features: [
      'Show up in "bakeries near me" searches',
      'Display hours, address, phone number',
      'Collect customer reviews',
      'Post updates and offers',
    ],
  },
  {
    id: 'google-merchant',
    name: 'Google Merchant Center',
    description: 'List products on Google Shopping',
    icon: FaShoppingCart,
    iconBg: 'bg-white',
    iconColor: 'text-[#4285F4]',
    status: 'not_connected',
    setupUrl: 'https://merchants.google.com',
    features: [
      'Show products in Google Shopping',
      'Enable "Order with Google"',
      'Sync menu items automatically',
      'Track product performance',
    ],
  },
  {
    id: 'google-analytics-4',
    name: 'Google Analytics (GA4)',
    description: 'See visits to your public website and where traffic comes from',
    icon: FaGoogle,
    iconBg: 'bg-white',
    iconColor: 'text-[#4285F4]',
    status: 'not_connected',
    setupUrl: 'https://analytics.google.com/',
    features: [
      'Visitor counts over time',
      'Popular pages (menu, gallery, order, etc.)',
      'Traffic sources: search, social, direct, referrals',
      'Counts only the customer-facing site — not your admin pages',
    ],
  },
  {
    id: 'facebook',
    name: 'Facebook Shop',
    description: 'Show your menu in Facebook Shop tab',
    icon: FaFacebook,
    iconBg: 'bg-[#1877F2]',
    iconColor: 'text-white',
    status: 'not_connected',
    setupUrl: 'https://business.facebook.com/commerce',
    features: [
      'Menu appears in Facebook Shop tab',
      'Products sync automatically from your menu',
      'Customers browse without leaving Facebook',
      'Links back to your website for ordering',
    ],
  },
  {
    id: 'instagram',
    name: 'Instagram Shopping',
    description: 'Tag products in your Instagram posts',
    icon: FaInstagram,
    iconBg: 'bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF]',
    iconColor: 'text-white',
    status: 'not_connected',
    setupUrl: 'https://business.instagram.com',
    features: [
      'Tag products in posts & stories',
      'Shop tab on Instagram profile',
      'Menu syncs automatically from your feed',
      'Customers can tap to see product details',
    ],
  },
];

// Status badge component
function StatusBadge({ status }: { status: IntegrationStatus }) {
  if (status === 'connected') {
    return (
      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
        <HiCheckCircle className="w-4 h-4" />
        Connected
      </span>
    );
  }
  
  return (
    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
      <HiExclamationCircle className="w-4 h-4" />
      Not Connected
    </span>
  );
}

// Feed status type
interface FeedStatus {
  feeds: {
    google: string;
    meta: string;
  };
  cache: {
    is_valid: boolean;
    last_updated: string | null;
    expires_in_minutes: number;
  };
}

// Square OAuth status type
interface SquareOAuthStatus {
  connected: boolean;
  businessName?: string;
  merchantId?: string;
  locationId?: string;
  connectedAt?: string;
  tokenExpired?: boolean;
  message?: string;
}

// localStorage key for manual integration status
const INTEGRATION_STATUS_KEY = 'integration_status';

// Get manual integration status from localStorage
function getManualIntegrationStatus(): Record<string, IntegrationStatus> {
  try {
    const stored = localStorage.getItem(INTEGRATION_STATUS_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // Invalid JSON, ignore
  }
  return {};
}

// Save manual integration status to localStorage
function saveManualIntegrationStatus(integrationId: string, status: IntegrationStatus) {
  try {
    const current = getManualIntegrationStatus();
    current[integrationId] = status;
    localStorage.setItem(INTEGRATION_STATUS_KEY, JSON.stringify(current));
  } catch {
    // Failed to save, ignore
  }
}

export default function Integrations() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [feedStatus, setFeedStatus] = useState<FeedStatus | null>(null);
  const [feedLoading, setFeedLoading] = useState(true);
  
  // Square OAuth state
  const [squareStatus, setSquareStatus] = useState<SquareOAuthStatus | null>(null);
  const [squareLoading, setSquareLoading] = useState(true);
  const [authUrl, setAuthUrl] = useState<string | null>(null);
  const [squareMessage, setSquareMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [pushCatalogLoading, setPushCatalogLoading] = useState(false);
  const [cleanupDuplicatesLoading, setCleanupDuplicatesLoading] = useState(false);
  const [refreshTokenLoading, setRefreshTokenLoading] = useState(false);
  const [catalogAudit, setCatalogAudit] = useState<{
    stalePortalCount?: number;
    squareOnlyCount?: number;
    unlinkedPortalCount?: number;
    linkedPortalCount?: number;
    duplicateGroupsCount?: number;
    duplicateGroups?: Array<{ name: string; category: string; count: number; itemIds: string[] }>;
    stalePortalLinks?: Array<{ name: string; square_catalog_id: string }>;
    squareOnlyList?: Array<{ id: string; name: string; category: string }>;
  } | null>(null);
  const [catalogAuditLoading, setCatalogAuditLoading] = useState(false);

  // Manual integration status (from localStorage)
  const [manualStatus, setManualStatus] = useState<Record<string, IntegrationStatus>>({});
  
  // Google Merchant Center feed validation
  const [merchantFeedValid, setMerchantFeedValid] = useState<boolean | null>(null);
  const [merchantFeedChecking, setMerchantFeedChecking] = useState(false);

  const websiteUrl = 'https://nannybbakery.ca';
  // Use relative URL in production (same domain), fallback to empty string for relative paths
  const apiUrl = import.meta.env.VITE_API_URL || '';

  // Check for OAuth callback messages in URL
  useEffect(() => {
    const success = searchParams.get('success');
    const error = searchParams.get('error');
    
    if (success) {
      setSquareMessage({ type: 'success', text: success });
      // Clear the URL params
      setSearchParams({});
      // Refresh Square status
      fetchSquareStatus();
    } else if (error) {
      setSquareMessage({ type: 'error', text: error });
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  // Load manual integration status from localStorage on mount
  useEffect(() => {
    const stored = getManualIntegrationStatus();
    setManualStatus(stored);
  }, []);

  // Check Google Merchant Center feed validity
  const checkMerchantFeed = async () => {
    setMerchantFeedChecking(true);
    try {
      const feedUrl = feedStatus?.feeds.google || `${websiteUrl}/api/feed/google.xml`;
      const response = await fetch(feedUrl, { method: 'HEAD' });
      // Check if feed is accessible and returns XML
      if (response.ok) {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('xml')) {
          setMerchantFeedValid(true);
        } else {
          // Try full fetch to verify XML content
          const fullResponse = await fetch(feedUrl);
          const text = await fullResponse.text();
          if (text.trim().startsWith('<?xml') || text.trim().startsWith('<rss')) {
            setMerchantFeedValid(true);
          } else {
            setMerchantFeedValid(false);
          }
        }
      } else {
        setMerchantFeedValid(false);
      }
    } catch (error) {
      console.error('Failed to check merchant feed:', error);
      setMerchantFeedValid(false);
    } finally {
      setMerchantFeedChecking(false);
    }
  };

  // Check merchant feed when feed status is loaded (only once)
  useEffect(() => {
    if (feedStatus && merchantFeedValid === null && !merchantFeedChecking) {
      checkMerchantFeed();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedStatus]);

  // Fetch feed status and Square status on mount
  useEffect(() => {
    fetchFeedStatus();
    fetchSquareStatus();
  }, []);

  // Auto-generate authorization URL only if Square is not connected OR token is expired
  useEffect(() => {
    if (!squareLoading && squareStatus) {
      // Only generate URL if we actually need it (not connected or expired)
      // Don't generate if connected and token is valid - all features are already enabled
      if ((!squareStatus.connected || squareStatus.tokenExpired) && !authUrl) {
        getAuthorizationUrl();
      }
      // Clear authUrl if connected and token is valid (no longer needed)
      if (squareStatus.connected && !squareStatus.tokenExpired && authUrl) {
        setAuthUrl(null);
      }
    }
  }, [squareLoading, squareStatus, authUrl]);

  // Fetch Square OAuth status
  const fetchSquareStatus = async () => {
    try {
      setSquareLoading(true);
      const response = await adminFetch(`${apiUrl}/api/oauth/status`);
      if (response.ok) {
        const data = await response.json();
        setSquareStatus(data);
      }
    } catch (error) {
      console.error('Failed to fetch Square status:', error);
    } finally {
      setSquareLoading(false);
    }
  };

  // Get authorization URL
  const getAuthorizationUrl = async () => {
    try {
      const response = await adminFetch(`${apiUrl}/api/oauth/authorize`);
      if (response.ok) {
        const data = await response.json();
        setAuthUrl(data.authorizationUrl);
      }
    } catch (error) {
      console.error('Failed to get authorization URL:', error);
      setSquareMessage({ type: 'error', text: 'Failed to generate authorization link' });
    }
  };

  // Refresh Square access token (when expired; uses stored refresh token, no re-login)
  const refreshSquareToken = async () => {
    try {
      setRefreshTokenLoading(true);
      setSquareMessage(null);
      const response = await adminFetch(`${apiUrl}/api/oauth/refresh`, { method: 'POST' });
      const data = await response.json().catch(() => ({}));
      if (response.ok && data.success) {
        setSquareMessage({ type: 'success', text: 'Connection refreshed. Square is connected again.' });
        await fetchSquareStatus();
      } else {
        setSquareMessage({ type: 'error', text: data.error || 'Refresh failed. Use Re-authorize below.' });
      }
    } catch (error) {
      console.error('Refresh token failed:', error);
      setSquareMessage({ type: 'error', text: 'Failed to refresh. Use Re-authorize below.' });
    } finally {
      setRefreshTokenLoading(false);
    }
  };

  // Disconnect Square
  const disconnectSquare = async () => {
    if (!confirm('Are you sure you want to disconnect Square? You will need to re-authorize to reconnect.')) {
      return;
    }
    
    try {
      const response = await adminFetch(`${apiUrl}/api/oauth/disconnect`, { method: 'POST' });
      if (response.ok) {
        setSquareStatus({ connected: false });
        setSquareMessage({ type: 'success', text: 'Square disconnected successfully' });
        setAuthUrl(null);
      }
    } catch (error) {
      console.error('Failed to disconnect Square:', error);
      setSquareMessage({ type: 'error', text: 'Failed to disconnect Square' });
    }
  };

  // Push all menu items to Square (Portal → Square)
  const pushMenuToSquare = async () => {
    try {
      setPushCatalogLoading(true);
      setSquareMessage(null);
      const response = await adminFetch(`${apiUrl}/api/square/push-catalog`, { method: 'POST' });
      const data = await response.json().catch(() => ({}));
      if (response.ok && data.success) {
        const r = data.results || {};
        const parts = [];
        if (r.pushed) parts.push(`${r.pushed} pushed`);
        if (r.linked) parts.push(`${r.linked} linked to existing`);
        if (r.failed) parts.push(`${r.failed} failed`);
        const msg = parts.length ? parts.join(', ') : (data.message || 'Done.');
        setSquareMessage({ type: 'success', text: msg });
      } else {
        setSquareMessage({ type: 'error', text: data.error || data.details || 'Failed to push menu to Square' });
      }
    } catch (error) {
      console.error('Push menu to Square failed:', error);
      setSquareMessage({ type: 'error', text: 'Failed to push menu to Square' });
    } finally {
      setPushCatalogLoading(false);
    }
  };

  // Remove duplicate catalog items in Square (keeps one per name+category, prefers portal-linked)
  const cleanupDuplicatesInSquare = async () => {
    try {
      setCleanupDuplicatesLoading(true);
      setSquareMessage(null);
      const response = await adminFetch(`${apiUrl}/api/square/cleanup-duplicates`, { method: 'POST' });
      const data = await response.json().catch(() => ({}));
      if (response.ok && data.success) {
        const n = data.deleted ?? 0;
        const msg = n > 0
          ? `Removed ${n} duplicate item(s) from Square.`
          : (data.errors?.length ? 'Some items could not be removed.' : 'No duplicate items found in Square.');
        setSquareMessage({ type: 'success', text: msg });
        // Refresh sync status so duplicate count updates without clicking "Check sync status"
        if (n > 0) {
          fetchCatalogAudit().then(() => setSquareMessage({ type: 'success', text: msg }));
        }
      } else {
        setSquareMessage({ type: 'error', text: data.error || data.details || 'Failed to remove duplicates' });
      }
    } catch (error) {
      console.error('Cleanup duplicates failed:', error);
      setSquareMessage({ type: 'error', text: 'Failed to remove duplicates' });
    } finally {
      setCleanupDuplicatesLoading(false);
    }
  };

  const fetchCatalogAudit = async () => {
    try {
      setCatalogAuditLoading(true);
      setSquareMessage(null);
      const response = await adminFetch(`${apiUrl}/api/square/catalog-audit`);
      const data = await response.json().catch(() => ({}));
      if (response.ok && data.success) {
        setCatalogAudit({
          stalePortalCount: data.stalePortalCount,
          squareOnlyCount: data.squareOnlyCount,
          unlinkedPortalCount: data.unlinkedPortalCount,
          linkedPortalCount: data.linkedPortalCount,
          duplicateGroupsCount: data.duplicateGroupsCount,
          duplicateGroups: data.duplicateGroups,
          stalePortalLinks: data.stalePortalLinks,
          squareOnlyList: data.squareOnlyList,
        });
        const parts = [];
        if (data.stalePortalCount) parts.push(`${data.stalePortalCount} stale link(s) (run Push to re-link)`);
        if (data.duplicateGroupsCount) parts.push(`${data.duplicateGroupsCount} duplicate group(s) in Square`);
        if (data.squareOnlyCount) parts.push(`${data.squareOnlyCount} item(s) only in Square`);
        if (data.unlinkedPortalCount) parts.push(`${data.unlinkedPortalCount} portal item(s) not yet in Square`);
        if (data.linkedPortalCount) parts.push(`${data.linkedPortalCount} linked`);
        setSquareMessage({ type: parts.some(p => p.includes('stale')) ? 'error' : 'success', text: parts.length ? parts.join('. ') : 'Sync status loaded.' });
      } else {
        setSquareMessage({ type: 'error', text: data.error || 'Failed to load sync status' });
      }
    } catch (error) {
      console.error('Catalog audit failed:', error);
      setSquareMessage({ type: 'error', text: 'Failed to load sync status' });
    } finally {
      setCatalogAuditLoading(false);
    }
  };

  const fetchFeedStatus = async () => {
    try {
      setFeedLoading(true);
      const response = await adminFetch(`${apiUrl}/api/feed/status`);
      if (response.ok) {
        const data = await response.json();
        setFeedStatus(data);
      }
    } catch (error) {
      console.error('Failed to fetch feed status:', error);
    } finally {
      setFeedLoading(false);
    }
  };

  const invalidateCache = async () => {
    try {
      await adminFetch(`${apiUrl}/api/feed/invalidate`, { method: 'POST' });
      fetchFeedStatus();
    } catch (error) {
      console.error('Failed to invalidate cache:', error);
    }
  };

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Toggle manual integration status
  const toggleIntegrationStatus = (integrationId: string, currentStatus: IntegrationStatus) => {
    const newStatus: IntegrationStatus = currentStatus === 'connected' ? 'not_connected' : 'connected';
    saveManualIntegrationStatus(integrationId, newStatus);
    setManualStatus(prev => ({ ...prev, [integrationId]: newStatus }));
  };

  // Get effective status for an integration (manual override or dynamic check)
  const getEffectiveStatus = (integration: Integration): IntegrationStatus => {
    // GA4: driven only by build-time env (no "Mark connected" — avoids false positives)
    if (integration.id === 'google-analytics-4') {
      return isGa4MeasurementIdConfigured(import.meta.env.VITE_GA_MEASUREMENT_ID)
        ? 'connected'
        : 'not_connected';
    }

    // Check manual override first
    if (manualStatus[integration.id]) {
      return manualStatus[integration.id];
    }

    // Dynamic checks
    if (integration.id === 'google-merchant') {
      // If feed is valid, consider it connected
      if (merchantFeedValid === true) {
        return 'connected';
      }
    }

    // Default to base status
    return integration.status;
  };

  // Calculate counts with effective statuses
  const integrationsWithStatus = integrations.map(i => ({
    ...i,
    effectiveStatus: getEffectiveStatus(i)
  }));
  
  const connectedCount = integrationsWithStatus.filter(i => i.effectiveStatus === 'connected').length;
  const notConnectedCount = integrationsWithStatus.filter(i => i.effectiveStatus === 'not_connected').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Integrations</h1>
        <p className="text-gray-500 mt-1">Connect Google and social media to grow your business</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-green-50 rounded-xl p-4 text-center">
          <p className="text-3xl font-bold text-green-600">{connectedCount + (squareStatus?.connected ? 1 : 0)}</p>
          <p className="text-sm text-gray-600">Connected</p>
        </div>
        <div className="bg-gray-50 rounded-xl p-4 text-center">
          <p className="text-3xl font-bold text-gray-500">{notConnectedCount + (squareStatus?.connected ? 0 : 1)}</p>
          <p className="text-sm text-gray-600">Ready to Setup</p>
        </div>
      </div>

      {/* Square Message Banner */}
      {squareMessage && (
        <div className={`rounded-xl p-4 flex items-center justify-between ${
          squareMessage.type === 'success' 
            ? 'bg-green-50 border border-green-200' 
            : 'bg-red-50 border border-red-200'
        }`}>
          <div className="flex items-center gap-3">
            {squareMessage.type === 'success' 
              ? <HiCheckCircle className="w-6 h-6 text-green-500" />
              : <HiExclamationCircle className="w-6 h-6 text-red-500" />
            }
            <span className={squareMessage.type === 'success' ? 'text-green-700' : 'text-red-700'}>
              {squareMessage.text}
            </span>
          </div>
          <button 
            onClick={() => setSquareMessage(null)}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Dismiss message"
            title="Dismiss message"
          >
            <HiX className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Square POS Integration - PRIORITY! */}
      <div className={`rounded-xl p-5 border ${
        squareStatus?.connected 
          ? 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-200' 
          : 'bg-gradient-to-br from-purple-50 to-indigo-50 border-purple-200'
      }`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              squareStatus?.connected ? 'bg-green-500' : 'bg-black'
            }`}>
              <FaSquare className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 text-lg">Square POS</h3>
              <p className="text-sm text-gray-600">
                {squareStatus?.connected 
                  ? 'Online orders sync to your Square app'
                  : 'Connect to see online orders in Square'
                }
              </p>
            </div>
          </div>
          {squareLoading ? (
            <span className="text-gray-400 text-sm">Loading...</span>
          ) : squareStatus?.connected ? (
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-700">
              <HiCheckCircle className="w-4 h-4" />
              Connected
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-600">
              <HiExclamationCircle className="w-4 h-4" />
              Not Connected
            </span>
          )}
        </div>

        {squareStatus?.connected ? (
          // Connected state
          <div className="space-y-4">
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Business:</span>
                  <p className="font-medium text-gray-900">{squareStatus.businessName || 'Unknown'}</p>
                </div>
                <div>
                  <span className="text-gray-500">Location ID:</span>
                  <p className="font-mono text-xs text-gray-700">{squareStatus.locationId || 'N/A'}</p>
                </div>
                <div>
                  <span className="text-gray-500">Connected:</span>
                  <p className="text-gray-700">
                    {squareStatus.connectedAt 
                      ? new Date(squareStatus.connectedAt).toLocaleDateString()
                      : 'Unknown'
                    }
                  </p>
                </div>
                <div>
                  <span className="text-gray-500">Status:</span>
                  <p className={squareStatus.tokenExpired ? 'text-red-600' : 'text-green-600'}>
                    {squareStatus.tokenExpired ? '⚠️ Token Expired' : '✅ Active'}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={pushMenuToSquare}
                disabled={pushCatalogLoading}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors text-sm font-medium flex items-center gap-2"
              >
                {pushCatalogLoading ? (
                  <>
                    <HiRefresh className="w-4 h-4 animate-spin" />
                    Pushing...
                  </>
                ) : (
                  <>
                    <HiRefresh className="w-4 h-4" />
                    Push menu to Square
                  </>
                )}
              </button>
              <button
                onClick={cleanupDuplicatesInSquare}
                disabled={cleanupDuplicatesLoading}
                className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 transition-colors text-sm font-medium flex items-center gap-2"
              >
                {cleanupDuplicatesLoading ? (
                  <>
                    <HiRefresh className="w-4 h-4 animate-spin" />
                    Removing...
                  </>
                ) : (
                  'Remove duplicates in Square'
                )}
              </button>
              <button
                onClick={fetchCatalogAudit}
                disabled={catalogAuditLoading}
                className="px-4 py-2 bg-gray-100 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors text-sm font-medium"
              >
                {catalogAuditLoading ? (
                  <>
                    <HiRefresh className="w-4 h-4 animate-spin inline" />
                    Checking...
                  </>
                ) : (
                  'Check sync status'
                )}
              </button>
              <a
                href="https://squareup.com/dashboard"
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors text-center font-medium text-sm flex items-center gap-2"
              >
                <HiExternalLink className="w-4 h-4" />
                Open Square Dashboard
              </a>
              <button
                onClick={disconnectSquare}
                className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors text-sm"
              >
                Disconnect
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Push menu to Square sends all your website menu items to Square and re-links any stale links (e.g. after removing duplicates).
              Remove duplicates in Square deletes extra catalog items with the same name and category. Check sync status shows portal vs Square link state.
            </p>
            {catalogAudit && ((catalogAudit.stalePortalCount ?? 0) > 0 || (catalogAudit.squareOnlyCount ?? 0) > 0 || (catalogAudit.unlinkedPortalCount ?? 0) > 0 || (catalogAudit.duplicateGroupsCount ?? 0) > 0 || (catalogAudit.linkedPortalCount != null && catalogAudit.linkedPortalCount > 0)) && (
              <div className="mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200 text-xs text-gray-700 space-y-2">
                <div>
                  <span className="font-medium">Sync status: </span>
                  {(catalogAudit.stalePortalCount ?? 0) > 0 && (
                    <span>{catalogAudit.stalePortalCount} portal item(s) point to deleted Square IDs → run Push to Square to re-link. </span>
                  )}
                  {(catalogAudit.duplicateGroupsCount ?? 0) > 0 && (
                    <span>{catalogAudit.duplicateGroupsCount} duplicate group(s) in Square → run Remove duplicates to clean up. </span>
                  )}
                  {(catalogAudit.squareOnlyCount ?? 0) > 0 && (
                    <span>{catalogAudit.squareOnlyCount} item(s) only in Square (not in portal). </span>
                  )}
                  {(catalogAudit.unlinkedPortalCount ?? 0) > 0 && (
                    <span>{catalogAudit.unlinkedPortalCount} portal item(s) not yet in Square → run Push to add/link. </span>
                  )}
                  {catalogAudit.linkedPortalCount != null && catalogAudit.linkedPortalCount > 0 && (
                    <span>{catalogAudit.linkedPortalCount} linked OK.</span>
                  )}
                </div>
                {catalogAudit.duplicateGroups && catalogAudit.duplicateGroups.length > 0 && (
                  <div>
                    <span className="font-medium">Duplicate groups (same name + category):</span>
                    <ul className="mt-1 ml-3 list-disc max-h-24 overflow-y-auto">
                      {catalogAudit.duplicateGroups.map((g, i) => (
                        <li key={i}>
                          {g.name}{g.category ? ` (${g.category})` : ''} — {g.count} item(s)
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {catalogAudit.squareOnlyList && catalogAudit.squareOnlyList.length > 0 && (
                  <div>
                    <span className="font-medium">Items only in Square (delete in Square Dashboard or add in Menu to link):</span>
                    <ul className="mt-1 ml-3 list-disc max-h-32 overflow-y-auto">
                      {catalogAudit.squareOnlyList.map((item) => (
                        <li key={item.id}>
                          {item.name}
                          {item.category ? ` (${item.category})` : ''}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Reconnect Section - Only show if token is expired or needs re-authorization */}
            {squareStatus.tokenExpired && (
              <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                <div className="flex items-start gap-3">
                  <HiExclamationCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="font-medium text-amber-800 text-sm mb-1">Token Expired - Refresh or Re-authorize</h4>
                    <p className="text-xs text-amber-700 mb-3">
                      Your Square access token expired (normal after ~30 days). Try &quot;Refresh connection&quot; first—no login needed. If that fails, re-authorize with Square.
                    </p>
                    <div className="flex flex-wrap gap-2 mb-3">
                      <button
                        type="button"
                        onClick={refreshSquareToken}
                        disabled={refreshTokenLoading}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm font-medium flex items-center gap-2"
                      >
                        {refreshTokenLoading ? (
                          <>
                            <HiRefresh className="w-4 h-4 animate-spin" />
                            Refreshing...
                          </>
                        ) : (
                          <>
                            <HiRefresh className="w-4 h-4" />
                            Refresh connection
                          </>
                        )}
                      </button>
                    </div>
                    {authUrl ? (
                      <div className="space-y-3">
                        {/* Primary action - BIG prominent button that opens in new tab */}
                        <a
                          href={authUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block w-full px-6 py-3 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors font-semibold text-center shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                          onClick={() => {
                            // Optional: Show a helpful message
                            setTimeout(() => {
                              setSquareMessage({
                                type: 'success',
                                text: 'Square authorization page opened! After you approve, you\'ll be redirected back automatically.'
                              });
                            }, 500);
                          }}
                        >
                          <HiExternalLink className="w-5 h-5" />
                          Click Here to Re-authorize Square
                        </a>
                        
                        <p className="text-xs text-amber-700 text-center mt-2">
                          Or re-authorize in a new tab. Just click &quot;Allow&quot; and you&apos;re done!
                        </p>
                        
                        {/* Copy link option (secondary) */}
                        <div className="flex items-center justify-center gap-2 pt-2 border-t border-amber-200">
                          <button
                            onClick={() => copyToClipboard(authUrl, 'square-reconnect')}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-amber-700 bg-white border border-amber-300 rounded-lg hover:bg-amber-50 transition-colors text-xs font-medium"
                          >
                            {copiedId === 'square-reconnect' ? (
                              <>
                                <HiCheckCircle className="w-4 h-4 text-green-500" />
                                Copied!
                              </>
                            ) : (
                              <>
                                <HiClipboardCopy className="w-4 h-4" />
                                Copy Link
                              </>
                            )}
                          </button>
                          <span className="text-xs text-amber-600">
                            (if you need to do this later)
                          </span>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={getAuthorizationUrl}
                        className="w-full px-6 py-3 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors font-semibold flex items-center justify-center gap-2"
                      >
                        <HiRefresh className="w-5 h-5" />
                        Generate Authorization Link
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {/* All Features Enabled Message - Show when connected and token is valid */}
            {!squareStatus.tokenExpired && (
              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                <div className="flex items-start gap-3">
                  <HiCheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="font-medium text-green-800 text-sm mb-1">✅ All Square Features Enabled</h4>
                    <p className="text-xs text-green-700">
                      Your Square connection includes full access to: customer list, discounts, and inventory management. Everything is working perfectly!
                    </p>
                  </div>
                </div>
              </div>
            )}

            <p className="text-xs text-gray-500">
              💡 Online orders now appear in your Square POS app automatically!
            </p>
          </div>
        ) : (
          // Not connected state (includes token-expired: API returns connected: false when token expired)
          <div className="space-y-4">
            {/* When token expired, show Refresh connection first (no re-login); then Connect as fallback */}
            {squareStatus?.tokenExpired && (
              <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                <h4 className="font-medium text-amber-800 text-sm mb-1">Token expired – refresh to reconnect</h4>
                <p className="text-xs text-amber-700 mb-3">
                  Your Square link is still saved; the access token expired (normal after ~30 days). Click &quot;Refresh connection&quot; to fix without logging in again.
                </p>
                <button
                  type="button"
                  onClick={refreshSquareToken}
                  disabled={refreshTokenLoading}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm font-medium flex items-center gap-2"
                >
                  {refreshTokenLoading ? (
                    <>
                      <HiRefresh className="w-4 h-4 animate-spin" />
                      Refreshing...
                    </>
                  ) : (
                    <>
                      <HiRefresh className="w-4 h-4" />
                      Refresh connection
                    </>
                  )}
                </button>
              </div>
            )}

            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <h4 className="font-medium text-gray-900 mb-2">What you'll get:</h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start gap-2">
                  <HiCheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                  Online orders appear in your Square app
                </li>
                <li className="flex items-start gap-2">
                  <HiCheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                  Accept payments through Square
                </li>
                <li className="flex items-start gap-2">
                  <HiCheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                  Menu syncs automatically with Square catalog
                </li>
                <li className="flex items-start gap-2">
                  <HiCheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                  Manage everything from one place
                </li>
              </ul>
            </div>

            {authUrl ? (
              <div className="space-y-4">
                {/* Primary action - BIG clickable button for Tammy */}
                <a
                  href={authUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full px-6 py-4 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors font-semibold text-lg flex items-center justify-center gap-3 shadow-lg hover:shadow-xl"
                >
                  <HiExternalLink className="w-6 h-6" />
                  Click Here to Connect Square
                </a>
                
                <p className="text-center text-sm text-gray-600">
                  This will open Square in a new tab. After approving, you'll be redirected back.
                </p>
                
                {/* Secondary - copy link to send to someone else */}
                <div className="border-t border-gray-200 pt-4">
                  <p className="text-sm text-gray-600 mb-2 text-center">
                    Or copy this link to send to someone else:
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => copyToClipboard(authUrl, 'square-auth')}
                      className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium flex items-center justify-center gap-2"
                    >
                      {copiedId === 'square-auth' ? (
                        <>
                          <HiCheckCircle className="w-4 h-4 text-green-500" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <HiClipboardCopy className="w-4 h-4" />
                          Copy Link
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <button
                  onClick={getAuthorizationUrl}
                  className="w-full px-6 py-4 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors font-semibold text-lg flex items-center justify-center gap-3 shadow-lg hover:shadow-xl"
                >
                  <HiLink className="w-6 h-6" />
                  Generate Authorization Link
                </button>
                <p className="text-center text-xs text-gray-500">
                  Click to generate the Square connection link
                </p>
              </div>
            )}

            {/* Show Square actions as disabled so the option is visible; connect first to enable */}
            <div className="border-t border-gray-200 pt-4 mt-4">
              <p className="text-xs text-gray-500 mb-2 font-medium">After connecting, you can:</p>
              <div className="flex flex-wrap gap-2">
                <button disabled className="px-3 py-1.5 bg-gray-100 text-gray-400 rounded-lg text-sm cursor-not-allowed">
                  Push menu to Square
                </button>
                <button disabled className="px-3 py-1.5 bg-amber-100 text-amber-700/70 rounded-lg text-sm cursor-not-allowed" title="Connect Square first">
                  Remove duplicates in Square
                </button>
                <button disabled className="px-3 py-1.5 bg-gray-100 text-gray-400 rounded-lg text-sm cursor-not-allowed">
                  Check sync status
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">Connect Square above to enable these options.</p>
            </div>
          </div>
        )}
      </div>

      {/* Product Feeds Section - NEW! */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-5 border border-blue-100">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center">
              <FaRss className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Your Product Feeds</h3>
              <p className="text-sm text-gray-600">Ready to connect to Google & Facebook</p>
            </div>
          </div>
          <button
            onClick={invalidateCache}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-white rounded-lg transition-colors"
            title="Refresh feeds"
          >
            <HiRefresh className="w-5 h-5" />
          </button>
        </div>

        {feedLoading ? (
          <div className="text-center py-4 text-gray-500">Loading feed status...</div>
        ) : (
          <div className="space-y-3">
            {/* Google Feed */}
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <FaGoogle className="w-4 h-4 text-[#4285F4]" />
                  <span className="font-medium text-gray-900">Google Merchant Feed</span>
                </div>
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                  <HiCheckCircle className="w-3 h-3" />
                  Ready
                </span>
              </div>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs bg-gray-100 px-3 py-2 rounded font-mono text-gray-700 overflow-x-auto">
                  {feedStatus?.feeds.google || `${websiteUrl}/api/feed/google.xml`}
                </code>
                <button
                  onClick={() => copyToClipboard(feedStatus?.feeds.google || `${websiteUrl}/api/feed/google.xml`, 'google-feed')}
                  className="p-2 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded transition-colors"
                >
                  {copiedId === 'google-feed' ? <HiCheckCircle className="w-5 h-5 text-green-500" /> : <HiClipboardCopy className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Meta Feed */}
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <FaFacebook className="w-4 h-4 text-[#1877F2]" />
                  <span className="font-medium text-gray-900">Facebook/Instagram Feed</span>
                </div>
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                  <HiCheckCircle className="w-3 h-3" />
                  Ready
                </span>
              </div>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs bg-gray-100 px-3 py-2 rounded font-mono text-gray-700 overflow-x-auto">
                  {feedStatus?.feeds.meta || `${websiteUrl}/api/feed/meta.xml`}
                </code>
                <button
                  onClick={() => copyToClipboard(feedStatus?.feeds.meta || `${websiteUrl}/api/feed/meta.xml`, 'meta-feed')}
                  className="p-2 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded transition-colors"
                >
                  {copiedId === 'meta-feed' ? <HiCheckCircle className="w-5 h-5 text-green-500" /> : <HiClipboardCopy className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Cache Status */}
            {feedStatus?.cache && (
              <div className="flex items-center justify-between text-xs text-gray-500 pt-2">
                <span>
                  {feedStatus.cache.is_valid 
                    ? `Cache valid for ${feedStatus.cache.expires_in_minutes} more minutes`
                    : 'Cache expired - will refresh on next request'
                  }
                </span>
                {feedStatus.cache.last_updated && (
                  <span>
                    Last updated: {new Date(feedStatus.cache.last_updated).toLocaleTimeString()}
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        <p className="text-xs text-gray-500 mt-4">
          💡 These feeds automatically update when you change your menu. Product images are emitted as full https URLs (required by Google and Meta). Shipping is listed as pickup in store in Canada ($0)—there is no delivery; confirm pickup settings in Merchant Center / Meta if their UI asks for shipping rules.
        </p>
      </div>

      {/* Integration Cards */}
      <div className="space-y-4">
        {integrationsWithStatus.map(integration => {
          const Icon = integration.icon;
          const isExpanded = expandedId === integration.id;
          const effectiveStatus = integration.effectiveStatus;

          return (
            <div 
              key={integration.id}
              className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm"
            >
              {/* Card Header - div so inner "Mark Connected" can stay a button (no button-in-button) */}
              <div
                className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors cursor-pointer"
                onClick={() => setExpandedId(isExpanded ? null : integration.id)}
              >
                <div className="flex items-center gap-4">
                  {/* Brand Icon */}
                  <div className={`w-12 h-12 rounded-xl ${integration.iconBg} flex items-center justify-center shadow-sm`}>
                    <Icon className={`w-6 h-6 ${integration.iconColor}`} />
                  </div>
                  <div className="text-left">
                    <h3 className="font-semibold text-gray-900">{integration.name}</h3>
                    <p className="text-sm text-gray-500">{integration.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <StatusBadge status={effectiveStatus} />
                  {/* GA4 status comes from server build config — no manual toggle */}
                  {integration.id === 'google-analytics-4' ? (
                    <span className="text-xs text-gray-500 max-w-[140px] text-right" title="Developer sets VITE_GA_MEASUREMENT_ID on the server">
                      {effectiveStatus === 'connected' ? 'Tracking on' : 'Ask dev to enable'}
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleIntegrationStatus(integration.id, effectiveStatus);
                      }}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                        effectiveStatus === 'connected'
                          ? 'bg-green-100 text-green-700 hover:bg-green-200'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                      title={effectiveStatus === 'connected' ? 'Mark as not connected' : 'Mark as connected'}
                    >
                      {effectiveStatus === 'connected' ? '✓ Connected' : 'Mark Connected'}
                    </button>
                  )}
                </div>
              </div>

              {/* Expanded Content */}
              {isExpanded && (
                <div className="border-t border-gray-100 p-4 bg-gray-50">
                  {/* Features */}
                  <div className="mb-4">
                    <h4 className="font-medium text-gray-900 mb-3">What you'll get:</h4>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {integration.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                          <HiCheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Setup Guide */}
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <h4 className="font-medium text-gray-900 mb-3">Setup Guide:</h4>
                    
                    {integration.id === 'google-business' && (
                      <ol className="space-y-2 text-sm text-gray-600 list-decimal list-inside">
                        <li>Go to <a href={integration.setupUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-medium">Google Business Profile</a></li>
                        <li>Search for "Nanny B's Bakery" or add new business</li>
                        <li>Verify ownership (postcard, phone, or email)</li>
                        <li>Add hours: Tue-Fri 11am-4:30pm, Sat 10am-3pm</li>
                        <li>Add photos of your bakery and products</li>
                        <li>Add website URL (copy below)</li>
                      </ol>
                    )}

                    {integration.id === 'google-merchant' && (
                      <div>
                        <ol className="space-y-2 text-sm text-gray-600 list-decimal list-inside">
                          <li>Go to <a href={integration.setupUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-medium">Google Merchant Center</a></li>
                          <li>Sign in with your Google account</li>
                          <li>Verify and claim your website</li>
                          <li>Go to Products → Feeds → Add Feed</li>
                          <li>Select "Scheduled fetch" and paste your feed URL (copy from above)</li>
                          <li>Set fetch frequency to "Daily"</li>
                        </ol>
                        <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200">
                          <div className="flex items-center gap-2 text-green-700 text-sm font-medium">
                            <HiCheckCircle className="w-4 h-4" />
                            Your product feed is ready!
                          </div>
                          <p className="text-xs text-green-600 mt-1">
                            Use the Google Merchant Feed URL from the "Your Product Feeds" section above.
                          </p>
                        </div>
                        {/* Feed Validation Status */}
                        <div className="mt-3 flex items-center gap-2">
                          <button
                            onClick={checkMerchantFeed}
                            disabled={merchantFeedChecking}
                            className="px-3 py-1.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors disabled:opacity-50"
                          >
                            {merchantFeedChecking ? 'Checking...' : 'Check Feed Status'}
                          </button>
                          {merchantFeedValid === true && (
                            <span className="text-xs text-green-600 flex items-center gap-1">
                              <HiCheckCircle className="w-4 h-4" />
                              Feed is accessible
                            </span>
                          )}
                          {merchantFeedValid === false && (
                            <span className="text-xs text-red-600 flex items-center gap-1">
                              <HiExclamationCircle className="w-4 h-4" />
                              Feed not accessible
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    {integration.id === 'facebook' && (
                      <div>
                        <ol className="space-y-2 text-sm text-gray-600 list-decimal list-inside">
                          <li>Go to <a href={integration.setupUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-medium">Meta Commerce Manager</a> (business.facebook.com/commerce)</li>
                          <li>Sign in with the account that manages your Facebook Page</li>
                          <li>Click <strong>"Create Catalog"</strong> → Select <strong>"E-commerce"</strong></li>
                          <li>Name it: <strong>"Nanny B's Bakery Menu"</strong> → Click <strong>"Create"</strong></li>
                          <li>In your catalog, go to <strong>"Data Sources"</strong> tab → <strong>"Add Data Source"</strong></li>
                          <li>Select <strong>"Data Feed"</strong> (not manual upload)</li>
                          <li>Paste your feed URL: <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">{feedStatus?.feeds.meta || `${websiteUrl}/api/feed/meta.xml`}</code></li>
                          <li>Set <strong>"Update schedule"</strong> to <strong>"Daily"</strong> → Click <strong>"Save"</strong></li>
                          <li>Go to <strong>"Shops"</strong> tab → <strong>"Add Shop"</strong> → Select your Facebook Page</li>
                          <li>Choose your catalog → Click <strong>"Connect"</strong></li>
                        </ol>
                        <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200">
                          <div className="flex items-center gap-2 text-green-700 text-sm font-medium">
                            <HiCheckCircle className="w-4 h-4" />
                            Your menu feed is ready!
                          </div>
                          <p className="text-xs text-green-600 mt-1">
                            <strong>Result:</strong> Your menu will appear in a "Shop" tab on your Facebook Page. 
                            Menu updates automatically when you change items in Admin Portal.
                          </p>
                        </div>
                        <div className="mt-2 p-2 bg-blue-50 rounded border border-blue-200">
                          <p className="text-xs text-blue-700">
                            <strong>💡 Tip:</strong> Copy the feed URL from the "Your Product Feeds" section above, or use: <code className="text-xs bg-blue-100 px-1 rounded">{feedStatus?.feeds.meta || `${websiteUrl}/api/feed/meta.xml`}</code>
                          </p>
                        </div>
                      </div>
                    )}

                    {integration.id === 'google-analytics-4' && (
                      <div className="space-y-3 text-sm text-gray-600">
                        <p>
                          Your public pages already support GA4. When Humberto adds your Measurement ID to the
                          server (<code className="text-xs bg-gray-100 px-1 py-0.5 rounded">VITE_GA_MEASUREMENT_ID</code>),
                          this card shows <strong>Connected</strong> and visitors are counted automatically.
                        </p>
                        <ol className="space-y-2 list-decimal list-inside">
                          <li>
                            Open{' '}
                            <a href="https://analytics.google.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-medium">
                              Google Analytics
                            </a>
                            {' '}and sign in with the Google account you used to create the property.
                          </li>
                          <li>
                            Go to <strong>Reports</strong> → <strong>Acquisition</strong> → <strong>Traffic acquisition</strong> to see
                            where visitors came from (Google, Instagram, direct, etc.).
                          </li>
                          <li>
                            Use <strong>Engagement</strong> → <strong>Pages and screens</strong> to see which pages are viewed most.
                          </li>
                        </ol>
                        {effectiveStatus === 'not_connected' && (
                          <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 text-amber-900 text-xs">
                            Tracking is not active yet — ask your developer to set{' '}
                            <code className="bg-amber-100 px-1 rounded">VITE_GA_MEASUREMENT_ID=G-…</code> in hosting (e.g. Coolify)
                            and redeploy.
                          </div>
                        )}
                      </div>
                    )}

                    {integration.id === 'instagram' && (
                      <div>
                        <div className="mb-3 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                          <div className="flex items-center gap-2 text-yellow-700 text-sm font-medium">
                            <HiExclamationCircle className="w-4 h-4" />
                            Prerequisite
                          </div>
                          <p className="text-xs text-yellow-600 mt-1">
                            <strong>Set up Facebook Shop first!</strong> Instagram Shopping uses the same catalog. 
                            Complete Facebook Shop setup above before continuing.
                          </p>
                        </div>
                        <ol className="space-y-2 text-sm text-gray-600 list-decimal list-inside">
                          <li><strong>Convert to Business Account:</strong> Instagram app → Settings → Account → Switch to Professional Account → Choose "Business"</li>
                          <li><strong>Connect to Facebook:</strong> During setup, connect to your Facebook Page: "Nanny B's Bakery"</li>
                          <li><strong>Add Instagram to Catalog:</strong> In Meta Commerce Manager → "Shops" tab → Find your shop → Click "Add Sales Channel" → Select "Instagram"</li>
                          <li><strong>Follow prompts</strong> to connect your Instagram account</li>
                          <li><strong>Enable Shopping:</strong> Instagram app → Settings → Business → Shopping</li>
                          <li><strong>Select catalog:</strong> Choose "Nanny B's Bakery Menu" (same one used for Facebook)</li>
                          <li><strong>Submit for review:</strong> Wait 1-2 business days for approval</li>
                        </ol>
                        <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200">
                          <div className="flex items-center gap-2 text-green-700 text-sm font-medium">
                            <HiCheckCircle className="w-4 h-4" />
                            Once Approved
                          </div>
                          <p className="text-xs text-green-600 mt-1">
                            You can tag products in your Instagram posts and stories! A "Shop" tab will also appear on your Instagram profile.
                          </p>
                        </div>
                        <div className="mt-2 p-2 bg-blue-50 rounded border border-blue-200">
                          <p className="text-xs text-blue-700">
                            <strong>💡 Note:</strong> Instagram Shopping uses the same feed URL as Facebook Shop. 
                            No separate feed setup needed - it's all connected!
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex flex-wrap gap-3 mt-4 pt-4 border-t border-gray-100">
                      <a
                        href={integration.setupUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white 
                                   rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
                      >
                        <HiExternalLink className="w-4 h-4" />
                        {integration.id === 'google-analytics-4'
                          ? 'Open Google Analytics'
                          : `Open ${integration.name.split(' ')[0]}`}
                      </a>
                      {integration.id !== 'google-analytics-4' && (
                        <button
                          type="button"
                          onClick={() => copyToClipboard(websiteUrl, integration.id)}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 
                                     rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
                        >
                          <HiClipboardCopy className="w-4 h-4" />
                          {copiedId === integration.id ? 'Copied!' : 'Copy Website URL'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Why It Matters */}
      <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-5">
        <h3 className="font-semibold text-gray-900 mb-3">Why These Integrations Matter</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          <div className="flex items-start gap-3">
            <FaGoogle className="w-5 h-5 text-[#4285F4] flex-shrink-0 mt-0.5" />
            <p className="text-gray-600">
              <strong className="text-gray-900">46% of Google searches</strong> are local - 
              appear when people search "bakery near me"
            </p>
          </div>
          <div className="flex items-start gap-3">
            <FaFacebook className="w-5 h-5 text-[#1877F2] flex-shrink-0 mt-0.5" />
            <p className="text-gray-600">
              <strong className="text-gray-900">2.9 billion users</strong> - 
              share your creations and collect reviews
            </p>
          </div>
          <div className="flex items-start gap-3">
            <FaInstagram className="w-5 h-5 text-[#E4405F] flex-shrink-0 mt-0.5" />
            <p className="text-gray-600">
              <strong className="text-gray-900">Perfect for bakeries</strong> - 
              showcase beautiful cakes and treats visually
            </p>
          </div>
          <div className="flex items-start gap-3">
            <FaShoppingCart className="w-5 h-5 text-[#4285F4] flex-shrink-0 mt-0.5" />
            <p className="text-gray-600">
              <strong className="text-gray-900">Google Shopping</strong> - 
              get your products in front of buyers
            </p>
          </div>
        </div>
      </div>

      {/* Help */}
      <p className="text-center text-sm text-gray-500 py-2">
        Need help setting up? Call Humberto at your convenience
      </p>
    </div>
  );
}
